import { api } from './apiComponent/apiClient'
import { endpoints } from './endpoints'

/**
 * Fusionne les données de base avec une traduction
 * @param {Object} baseData - Données de base de l'entité
 * @param {Object} translation - Données de traduction
 * @param {string} type - Type d'entité: 'course', 'module', 'block', 'quiz'
 * @returns {Object} - Données fusionnées
 */
export function mergeTranslation(baseData, translation, type = 'course') {
  if (!baseData) return null
  if (!translation) return baseData

  const merged = { ...baseData }

  // Champs communs à toutes les entités
  if (translation.title !== undefined) merged.title = translation.title
  if (translation.description !== undefined) merged.description = translation.description

  // Champs spécifiques aux cours
  if (type === 'course') {
    if (translation.shortDescription !== undefined) merged.shortDescription = translation.shortDescription
    if (translation.requirements !== undefined) merged.requirements = translation.requirements
    if (translation.objectives !== undefined) merged.objectives = translation.objectives
  }

  // Champs spécifiques aux modules
  if (type === 'module') {
    // Modules n'ont que title et description
  }

  // Champs spécifiques aux blocs
  if (type === 'block') {
    if (translation.content !== undefined) {
      // Pour les blocs, le contenu peut être un objet { html: '...' } ou une string
      merged.content = translation.content
    }
  }

  // Champs spécifiques aux quiz
  if (type === 'quiz') {
    if (translation.instructions !== undefined) merged.instructions = translation.instructions
  }

  return merged
}

/**
 * Charge la traduction d'une entité et la fusionne avec les données de base
 * @param {string} type - Type d'entité: 'course', 'module', 'block', 'quiz'
 * @param {string} entityId - ID de l'entité
 * @param {Object} baseData - Données de base
 * @param {string} language - Langue ('fr' ou 'en')
 * @returns {Promise<Object>} - Données fusionnées
 */
export async function loadAndMergeTranslation(type, entityId, baseData, language = 'fr') {
  if (!baseData || !entityId || language === 'fr') {
    return baseData
  }

  try {
    let endpoint
    switch (type) {
      case 'course':
        endpoint = endpoints.getCourseTranslation(entityId, language)
        break
      case 'module':
        endpoint = endpoints.getModuleTranslation(entityId, language)
        break
      case 'block':
        endpoint = endpoints.getBlockTranslation(entityId, language)
        break
      case 'quiz':
        endpoint = endpoints.getQuizTranslation(entityId, language)
        break
      default:
        return baseData
    }

    const response = await api.get(endpoint)
    const translation = response?.data || response

    if (translation) {
      return mergeTranslation(baseData, translation, type)
    }
  } catch (error) {
    // Si la traduction n'existe pas, retourner les données de base
    console.debug(`Translation not found for ${type} ${entityId} in ${language}`, error)
  }

  return baseData
}

/**
 * Charge les traductions pour une liste d'entités
 * @param {string} type - Type d'entité: 'course', 'module', 'block', 'quiz'
 * @param {Array} entities - Liste des entités
 * @param {string} language - Langue ('fr' ou 'en')
 * @returns {Promise<Array>} - Liste des entités avec traductions
 */
export async function loadTranslationsForEntities(type, entities, language = 'fr') {
  if (!entities || entities.length === 0 || language === 'fr') {
    return entities || []
  }

  const promises = entities.map(async (entity) => {
    if (!entity?.id) return entity
    return await loadAndMergeTranslation(type, entity.id, entity, language)
  })

  return Promise.all(promises)
}

